import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  useMediaQuery,
  Box,
  useTheme,
  alpha,
  Popper,
  Paper,
  Fade,
} from '@mui/material';
import { Menu, ExpandLess, ExpandMore, Close } from '@mui/icons-material';
import { useGameMode } from '../../Providers/GameModes/GameModes.jsx';
import { Storage } from '../../utils.jsx';

const FIVE_LETTER_NOTICE_KEY = 'fiveLetterModeNotice';
const MAX_NOTICE_VIEWS = 3;
const NOTICE_DURATION_MS = 4500;

function getLocalDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function HamburgerMenu() {
  const theme = useTheme();
  const menuButtonRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState('25%');
  const [noticeOpen, setNoticeOpen] = useState(false);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { gameMode, changeGameMode, getAvailableGameModes } = useGameMode();
  const availableGameModes = getAvailableGameModes();

  useEffect(() => {
    if (isSmallScreen) {
      setDrawerWidth('75%');
    } else if (isMediumScreen) {
      setDrawerWidth('50%');
    } else {
      setDrawerWidth('25%');
    }
  }, [isSmallScreen, isMediumScreen]);

  useEffect(() => {
    const noticeHistory =
      Storage.getFromLocalStorage(FIVE_LETTER_NOTICE_KEY, {}) || {};

    if (gameMode === 'standard5') {
      if (!noticeHistory.hasVisitedChaindle5) {
        Storage.saveToLocalStorage(FIVE_LETTER_NOTICE_KEY, {
          ...noticeHistory,
          hasVisitedChaindle5: true,
        });
      }
      setNoticeOpen(false);
      return undefined;
    }

    if (gameMode !== 'standard4') {
      setNoticeOpen(false);
      return undefined;
    }

    const today = getLocalDateKey();
    const views = Number.isInteger(noticeHistory.views)
      ? noticeHistory.views
      : 0;

    if (
      noticeHistory.hasVisitedChaindle5 ||
      views >= MAX_NOTICE_VIEWS ||
      noticeHistory.lastShownDate === today
    ) {
      return undefined;
    }

    Storage.saveToLocalStorage(FIVE_LETTER_NOTICE_KEY, {
      views: views + 1,
      lastShownDate: today,
    });
    setNoticeOpen(true);

    const timer = window.setTimeout(
      () => setNoticeOpen(false),
      NOTICE_DURATION_MS,
    );
    return () => window.clearTimeout(timer);
  }, [gameMode]);

  const handleDrawerToggle = () => {
    setNoticeOpen(false);
    setDrawerOpen((isOpen) => !isOpen);
  };

  const handleNoticeClick = () => {
    setNoticeOpen(false);
    setDrawerOpen(true);
  };

  const determineInitialOpenMode = () => {
    for (let mode of Object.keys(availableGameModes)) {
      for (let gameDetails of Object.values(availableGameModes[mode])) {
        if (gameDetails.type === gameMode && !gameDetails.disabled) {
          return mode;
        }
      }
    }
    return null;
  };

  const [openMode, setOpenMode] = useState(determineInitialOpenMode);

  const handleModeClick = (type) => {
    changeGameMode(type);
    handleDrawerToggle();
  };

  const renderGameModes = () => {
    return Object.keys(availableGameModes).map((mode) => (
      <React.Fragment key={mode}>
        <ListItemButton
          onClick={() => setOpenMode(mode === openMode ? null : mode)}
          sx={{ mx: 1, mb: 0.5, borderRadius: '14px' }}
        >
          {availableGameModes[mode].icon}
          <ListItemText primary={mode} />
          {mode === openMode ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={mode === openMode} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {Object.entries(availableGameModes[mode]).filter(
              ([, gameDetails]) => typeof gameDetails.type === 'string',
            ).map(
              ([key, gameDetails]) => (
                <ListItemButton
                  key={key}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    paddingLeft: theme.spacing(6),
                    borderRadius: '14px',
                    backgroundColor:
                      gameDetails.type === gameMode
                        ? alpha(theme.palette.primary.main, 0.5)
                        : 'inherit',
                  }}
                  onClick={() => handleModeClick(gameDetails.type)}
                  selected={gameDetails.type === gameMode}
                  disabled={gameDetails.disabled}
                >
                  <ListItemText primary={gameDetails.description} />
                </ListItemButton>
              ),
            )}
          </List>
        </Collapse>
      </React.Fragment>
    ));
  };

  return (
    <div>
      <IconButton
        ref={menuButtonRef}
        color="inherit"
        onClick={handleDrawerToggle}
        aria-label="Open game modes"
        sx={{
          ml: { xs: 0.5, sm: 1 },
          width: 72,
          height: 42,
          px: 1,
          gap: 0.75,
          borderRadius: '14px',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) },
        }}
      >
        <Menu />
        <Box
          component="span"
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 25,
            height: 25,
            flexShrink: 0,
            borderRadius: '999px',
            fontSize: '0.82rem',
            fontWeight: 900,
            lineHeight: 1,
            color: theme.palette.secondary.contrastText,
            bgcolor: theme.palette.secondary.main,
            boxShadow: '0 3px 9px rgba(15, 23, 42, .18)',
            pointerEvents: 'none',
          }}
        >
          {gameMode.slice(-1)}
        </Box>
      </IconButton>
      <Popper
        open={noticeOpen && !drawerOpen}
        anchorEl={menuButtonRef.current}
        placement="bottom-start"
        transition
        sx={{ zIndex: theme.zIndex.tooltip }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={250}>
            <Paper
              role="status"
              onClick={handleNoticeClick}
              elevation={6}
              sx={{
                position: 'relative',
                mt: 1.5,
                ml: 0.5,
                px: 1.5,
                py: 1,
                maxWidth: '190px',
                cursor: 'pointer',
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.primary.main,
                border: `2px solid ${theme.palette.secondary.main}`,
                '@keyframes modeNoticePulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.06)' },
                },
                animation: 'modeNoticePulse 900ms ease-in-out 3',
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }}
            >
              <Typography
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  top: '-27px',
                  left: '4px',
                  fontSize: '1.75rem',
                  fontWeight: 900,
                  color: theme.palette.secondary.main,
                  lineHeight: 1,
                }}
              >
                ↑
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Chaindle 5 is here!
              </Typography>
              <Typography variant="caption">Tap to try it</Typography>
            </Paper>
          </Fade>
        )}
      </Popper>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            width: drawerWidth,
            maxWidth: 380,
            bgcolor: theme.custom.surfaceElevated,
            borderRight: `1px solid ${theme.custom.border}`,
            borderRadius: '0 24px 24px 0',
          },
        }}
      >
        <AppBar position="static" color="transparent" elevation={0}>
          <Toolbar sx={{ minHeight: 76 }}>
            <Typography variant="h6" sx={{ paddingLeft: theme.spacing(1) }}>
              Game Modes
            </Typography>
            <Box flexGrow={1} />
            <IconButton edge="end" color="inherit" onClick={handleDrawerToggle}>
              <Close />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box display="flex" flexDirection="column" height="100%">
          <List>
            <Box display="flex" flexDirection="column" height="100%">
              <List>{renderGameModes()}</List>
            </Box>
          </List>
        </Box>
      </Drawer>
    </div>
  );
}

export default HamburgerMenu;
